// src/pages/school/Exam/QuestionBankPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card, Row, Col, Button, Table, Badge, Form, InputGroup,
  Spinner, Alert, Toast, ToastContainer, Modal, Dropdown, ButtonGroup
} from "react-bootstrap";
import {
  collection, doc, onSnapshot, query, orderBy, limit, where,
  addDoc, setDoc, getDoc, getDocs, serverTimestamp, updateDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../../../services/firebase";
import useSchool from "../../../hooks/useSchool";

/* ---------- Constants ---------- */
const DIFFICULTY = ["Easy","Medium","Hard"];
const TYPE_LABEL = (t) => (t || "mcq").toUpperCase();

/* ---------- Small helpers ---------- */
const stripHtml = (html = "") =>
  String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const getClassLabel = (q) => q.className || q.class || q.grade || q.classId || "";
const getChapterLabel = (q) => q.chapter || q.chapterName || q.chapterNo || "";
const getTopicLabel = (q) => q.topic || "";

/* ---------- Assertion-Reason helpers ---------- */
const AR_DEFAULTS = [
  { key: "A", text: "Both Assertion (A) and Reason (R) are true and R is the correct explanation of A." },
  { key: "B", text: "Both Assertion (A) and Reason (R) are true but R is NOT the correct explanation of A." },
  { key: "C", text: "Assertion (A) is true but Reason (R) is false." },
  { key: "D", text: "Assertion (A) is false but Reason (R) is true." },
];

const arAnswerText = (ans = "A", custom = null) => {
  const src = Array.isArray(custom) && custom.length ? custom : AR_DEFAULTS;
  const found = src.find(x => String(x.key).toUpperCase() === String(ans).toUpperCase());
  return found?.text || "";
};

const az = (i) => String.fromCharCode(65 + i); // A,B,C,...

/* ---------- Fingerprint helpers (robust for MATCH & AR types) ---------- */
const decodeHtml = (s = "") => {
  const el = document.createElement("textarea");
  el.innerHTML = s;
  return el.value;
};
const normalizeHtml = (html = "") =>
  decodeHtml(String(html || ""))
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;|\u00a0/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

// FNV-1a 32-bit
const hash32 = (s = "") => {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, "0");
};

const buildFingerprint = (q) => {
  const type = q.questionType || "mcq";

  // Primary stem per type
  let stem = "";
  if (type === "case_based") {
    stem = normalizeHtml(q.passageHtml);
  } else if (type === "assertion_reason") {
    stem = `${normalizeHtml(q.assertionHtml)} || ${normalizeHtml(q.reasonHtml)}`;
  } else {
    stem = normalizeHtml(q.questionHtml);
  }

  // Options across types
  let optionsStr = "";
  if (type === "match") {
    if (Array.isArray(q.matchOptions) && q.matchOptions.length) {
      optionsStr = q.matchOptions.map(arr => (arr || []).join(",")).join("|");
    } else {
      // legacy: concrete pairs
      const mp = (q.matchPairs || [])
        .map(r => `${normalizeHtml(r.leftHtml)}::${normalizeHtml(r.rightHtml)}`)
        .join("|");
      optionsStr = mp;
    }
  } else if (type === "assertion_reason") {
    // Include explicit AR options if stored; otherwise include the correct letter
    const arOpts = (q.options || []).map(o => normalizeHtml(o?.value || "")).join("|");
    optionsStr = arOpts || String(q.arAnswer || "");
  } else {
    optionsStr = (q.options || [])
      .map(o => normalizeHtml(o?.value || ""))
      .join("|");
  }

  const stmts = (q.statements || [])
    .map(s => `${normalizeHtml(s.html)}:${s.isTrue ? "1" : "0"}`)
    .join("|");

  const caseSubs = (q.caseSubQs || [])
    .map(sq => {
      const sqOpts = (sq.options || []).map(o => normalizeHtml(o.value || "")).join(",");
      return `${normalizeHtml(sq.questionHtml)}::${sqOpts}::${sq.correctKey || ""}`;
    })
    .join("|");

  const parts = [
    q.subject || "",
    getChapterLabel(q) || "",
    getTopicLabel(q) || "",
    type,
    stem,
    optionsStr,
    String(q.matchPairCount || 0),
    stmts,
    caseSubs,
    q.trueFalseAnswer || "",
    q.arAnswer || "",
    normalizeHtml(q.descPartHtml || ""),
    String(q.correctOptionIndex ?? "")
  ];
  return parts.join("||");
};

// Deterministic question id inside a template
const stableDocId = (templateId, fingerprint) =>
  `tpl_${templateId}_${hash32(fingerprint)}`;

/* ---------- MATCH label helpers for renderer ---------- */
const leftLabel = (idx) => {
  let n = idx + 1, s = "";
  while (n > 0) { n--; s = String.fromCharCode(97 + (n % 26)) + s; n = Math.floor(n / 26); }
  return s;
};
const toRoman = (num) => {
  const map = [[1000,"m"],[900,"cm"],[500,"d"],[400,"cd"],[100,"c"],[90,"xc"],[50,"l"],[40,"xl"],[10,"x"],[9,"ix"],[5,"v"],[4,"iv"],[1,"i"]];
  let res = "", n = Math.max(1, Math.floor(num));
  for (const [v,s] of map) while (n >= v) { res += s; n -= v; }
  return res;
};
const rightLabel = (idx) => toRoman(idx + 1);
const formatMatchOptionText = (arr = []) =>
  (!arr.length || arr.some(v => v == null))
    ? "—"
    : arr.map((rIdx, lIdx) => `(${leftLabel(lIdx)})-(${rightLabel(rIdx)})`).join(", ");

/* ---------- Question details renderer ---------- */
function QuestionDetails({ q }) {
  const type = q.questionType || "mcq";

  const renderOptions = (opts = []) => (
    <ol className="mb-2" type="A">
      {opts.map((o, i) => (
        <li key={o.key || i}>
          <div dangerouslySetInnerHTML={{ __html: o.value || "<em class='text-muted'>—</em>" }} />
          {(o.isCorrect || q.correctKey === (o.key || az(i))) && (
            <Badge bg="success" className="ms-2">Correct</Badge>
          )}
        </li>
      ))}
    </ol>
  );

  const renderArBlock = () => {
    // Prefer custom options if provided; else render standard AR options
    const customOpts = Array.isArray(q.options) && q.options.length > 0
      ? q.options.map((o, i) => ({ key: o.key || az(i), text: stripHtml(o.value || "") }))
      : AR_DEFAULTS;

    const answer = (q.arAnswer || (customOpts.find(o => o.isCorrect)?.key)) || "A";
    const answerText = arAnswerText(answer, customOpts);

    return (
      <>
        <div><strong>Assertion (A):</strong></div>
        <div className="mb-2" dangerouslySetInnerHTML={{ __html: q.assertionHtml || "" }} />
        <div><strong>Reason (R):</strong></div>
        <div className="mb-2" dangerouslySetInnerHTML={{ __html: q.reasonHtml || "" }} />

        <ol className="mb-2" type="A">
          {customOpts.map((o) => (
            <li key={o.key}>
              <div>{o.text || "—"}</div>
              {String(o.key).toUpperCase() === String(answer).toUpperCase() && (
                <Badge bg="success" className="ms-2">Correct</Badge>
              )}
            </li>
          ))}
        </ol>

        <div className="mb-2">
          <Badge bg="info">
            Answer: {String(answer).toUpperCase()} — {answerText || "—"}
          </Badge>
        </div>
      </>
    );
  };

  const renderCaseBlock = () => {
    const subs = Array.isArray(q.caseSubQs) ? q.caseSubQs : [];

    return (
      <div className="mb-2">
        <div className="text-muted">Passage / Case</div>
        <div className="mb-2" dangerouslySetInnerHTML={{ __html: q.passageHtml || "" }} />

        {subs.length === 0 && <div className="text-muted">No sub-questions.</div>}

        {subs.map((sq, i) => {
          const sqOpts = Array.isArray(sq.options) ? sq.options : [];
          // Derive correct key if only flags are set
          const derivedKey = (sq.correctKey ||
            sqOpts.find(o => o.isCorrect)?.key ||
            (sqOpts.find((o, idx) => o.isCorrect) ? az(sqOpts.findIndex(o => o.isCorrect)) : null)
          );

          return (
            <div className="mb-3 p-2 border rounded" key={sq.id || i}>
              <div className="d-flex align-items-center justify-content-between">
                <small className="text-muted">Sub-question {i+1}{typeof sq.marks === "number" ? ` · ${sq.marks} mark${sq.marks === 1 ? "" : "s"}` : ""}</small>
                {derivedKey && (
                  <Badge bg="success">Answer: {derivedKey}</Badge>
                )}
              </div>

              <div className="mt-1" dangerouslySetInnerHTML={{ __html: sq.questionHtml || "" }} />

              {sqOpts.length > 0 ? (
                <ol className="mt-2" type="A">
                  {sqOpts.map((o, oi) => {
                    const key = o.key || az(oi);
                    const isCorrect = (sq.correctKey ? (sq.correctKey === key) : !!o.isCorrect);
                    return (
                      <li key={key}>
                        <div dangerouslySetInnerHTML={{ __html: o.value || "<em class='text-muted'>—</em>" }} />
                        {isCorrect && <Badge bg="success" className="ms-2">Correct</Badge>}
                      </li>
                    );
                  })}
                </ol>
              ) : (
                <div className="text-muted mt-1">No options.</div>
              )}

              {sq.explanationHtml && (
                <div className="mt-2">
                  <div className="text-muted">Explanation</div>
                  <div className="text-muted" dangerouslySetInnerHTML={{ __html: sq.explanationHtml }} />
                </div>
              )}
            </div>
          );
        })}

        {q.explanationHtml && (
          <div className="mt-2">
            <div className="text-muted">Overall Explanation</div>
            <div className="text-muted" dangerouslySetInnerHTML={{ __html: q.explanationHtml }} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="small">
      {/* Stem / Passage */}
      {type !== "case_based" && type !== "assertion_reason" && (
        <div className="mb-2" dangerouslySetInnerHTML={{ __html: q.questionHtml || "" }} />
      )}

      {/* Type specific */}
      {["mcq","multi_correct","mcq_descriptive"].includes(type) && renderOptions(q.options || [])}

      {type === "mcq_descriptive" && q.descPartHtml && (
        <>
          <div className="text-muted">Descriptive Part</div>
          <div className="mb-2" dangerouslySetInnerHTML={{ __html: q.descPartHtml }} />
        </>
      )}

      {type === "true_false" && (
        <div className="mb-2"><Badge bg="info">Answer: {q.trueFalseAnswer || "True"}</Badge></div>
      )}

      {type === "match" && (
        <>
          {Array.isArray(q.matchPairs) && q.matchPairs.length ? (
            <Table size="sm" bordered className="mb-2">
              <thead><tr><th>List-I</th><th>List-II</th></tr></thead>
              <tbody>
                {q.matchPairs.filter(r => r.leftHtml || r.rightHtml).map((r, i) => (
                  <tr key={r.id || i}>
                    <td dangerouslySetInnerHTML={{ __html: r.leftHtml || "" }} />
                    <td dangerouslySetInnerHTML={{ __html: r.rightHtml || "" }} />
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div className="mb-2">
              <div className="text-muted">Pairs: {q.matchPairCount || "-"}</div>
              {Array.isArray(q.matchOptions) && q.matchOptions.length > 0 && (
                <div className="mt-1">
                  {q.matchOptions.map((arr, i) => (
                    <div key={i} className="mb-1">
                      <Badge bg={q.correctOptionIndex === i ? "success" : "secondary"} className="me-2">
                        Option {i+1}
                      </Badge>
                      <span className="text-muted">{formatMatchOptionText(arr)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {type === "assertion_reason" && renderArBlock()}

      {type === "statement_based" && (
        <div className="mb-2">
          {(q.statements || []).map((s, i) => (
            <div key={s.id || i} className="mb-1">
              <strong>{s.key || az(i)}.</strong>{" "}
              <span dangerouslySetInnerHTML={{ __html: s.html || "" }} />
              {s.isTrue && <Badge bg="success" className="ms-2">True</Badge>}
            </div>
          ))}
        </div>
      )}

      {type === "case_based" && renderCaseBlock()}

      {/* Explanation (for non-case types) */}
      {type !== "case_based" && q.explanationHtml && (
        <>
          <div className="text-muted">Explanation</div>
          <div className="text-muted" dangerouslySetInnerHTML={{ __html: q.explanationHtml }} />
        </>
      )}
    </div>
  );
}

/* ---------- Main ---------- */
export default function QuestionBankPage() {
  const { schoolId, school, loading: schoolLoading } = useSchool();
  const nav = useNavigate();

  const isActiveSchool = useMemo(() => {
    if (!school) return false;
    if (typeof school.active === "boolean") return school.active;
    const status = String(school.status || "").trim().toLowerCase();
    return status === "active";
  }, [school]);

  // Data
  const [loading, setLoading] = useState(true);
  const [sharedBank, setSharedBank] = useState([]); // /questionbank
  const [localBank, setLocalBank] = useState([]);   // /schools/{sid}/questionbank
  const [templates, setTemplates] = useState([]);
  const [tplLoading, setTplLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState("");
  const [fClass, setFClass] = useState("");
  const [fSubject, setFSubject] = useState("");
  const [fChapter, setFChapter] = useState("");
  const [fTopic, setFTopic] = useState("");
  const [fType, setFType] = useState("");
  const [fDiff, setFDiff] = useState("");
  const [showShared, setShowShared] = useState(true);
  const [showLocal, setShowLocal] = useState(true);

  // Selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectAllOnPage, setSelectAllOnPage] = useState(false);

  // Toast + modal
  const [toast, setToast] = useState({ show: false, msg: "" });
  const showToast = (msg) => setToast({ show: true, msg });
  const [newTplOpen, setNewTplOpen] = useState(false);
  const [newTplName, setNewTplName] = useState("");
  const [newTplType, setNewTplType] = useState("objective");

  /* ---------- Load SHARED bank (/questionbank) ---------- */
  useEffect(() => {
    setLoading(true);
    const ref = collection(db, "questionbank");
    const qy = query(ref, orderBy("updatedAt", "desc"), limit(2000));
    const unsub = onSnapshot(
      qy,
      (snap) => {
        setSharedBank(snap.docs.map(d => ({ id: d.id, _src: "shared", ...(d.data() || {}) })));
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, []);

  /* ---------- Load LOCAL bank (/schools/{sid}/questionbank) ---------- */
  useEffect(() => {
    if (!schoolId) return;
    const ref = collection(db, "schools", schoolId, "questionbank");
    const qy = query(ref, orderBy("updatedAt", "desc"), limit(2000));
    const unsub = onSnapshot(
      qy,
      (snap) => setLocalBank(snap.docs.map(d => ({ id: d.id, _src: "local", ...(d.data() || {}) }))),
      // If collection doesn’t exist or permission denied, just ignore
      () => setLocalBank([])
    );
    return () => unsub();
  }, [schoolId]);

  /* ---------- Load templates ---------- */
  useEffect(() => {
    if (!schoolId) return;
    setTplLoading(true);
    const ref = collection(db, "schools", schoolId, "examTemplates");
    const qy = query(ref, orderBy("updatedAt", "desc"), limit(50));
    const unsub = onSnapshot(
      qy,
      (snap) => {
        setTemplates(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
        setTplLoading(false);
      },
      () => setTplLoading(false)
    );
    return () => unsub();
  }, [schoolId]);

  /* ---------- Merge + de-dup ---------- */
  const allBank = useMemo(() => {
    const combined = [
      ...sharedBank,
      ...localBank,
    ];

    // Filter by source on toggle
    const bySource = combined.filter(q =>
      (showShared && q._src === "shared") || (showLocal && q._src === "local")
    );

    // Apply filters + dedupe by dedupeKey or fingerprint
    const needle = search.trim().toLowerCase();
    const pass = (q) => {
      if (fClass && getClassLabel(q) !== fClass) return false;
      if (fSubject && q.subject !== fSubject) return false;
      if (fChapter && getChapterLabel(q) !== fChapter) return false;
      if (fTopic && getTopicLabel(q) !== fTopic) return false;
      if (fType && (q.questionType || "mcq") !== fType) return false;
      if (fDiff && q.difficulty !== fDiff) return false;
      if (needle) {
        const plain = stripHtml(
          q.questionHtml ||
          q.passageHtml ||
          `${q.assertionHtml || ""} ${q.reasonHtml || ""}`
        );
        const hay = [
          getClassLabel(q),
          q.subject || "",
          getChapterLabel(q),
          getTopicLabel(q),
          q.questionType || "",
          q.difficulty || "",
          plain,
        ].join(" ").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    };

    const seen = new Set();
    const out = [];
    for (const q of bySource) {
      if (!pass(q)) continue;
      const fp = q.dedupeKey || buildFingerprint(q);
      if (seen.has(fp)) continue;
      seen.add(fp);
      out.push({ ...q, _fp: fp });
    }
    return out;
  }, [sharedBank, localBank, showShared, showLocal, fClass, fSubject, fChapter, fTopic, fType, fDiff, search]);

  /* ---------- Facets ---------- */
  const classOptions = useMemo(() => {
    const s = new Set(allBank.map(getClassLabel).filter(Boolean));
    return Array.from(s).sort();
  }, [allBank]);

  const subjects = useMemo(() => {
    const set = new Set(allBank.map((q) => q.subject).filter(Boolean));
    return Array.from(set).sort();
  }, [allBank]);

  const chapterOptions = useMemo(() => {
    const s = new Set(allBank.map(getChapterLabel).filter(Boolean));
    return Array.from(s).sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
  }, [allBank]);

  const topicOptions = useMemo(() => {
    const s = new Set(allBank.map(getTopicLabel).filter(Boolean));
    return Array.from(s).sort();
  }, [allBank]);

  const subjectCounts = useMemo(() => {
    const m = {};
    allBank.forEach((q) => {
      const s = q.subject || "—";
      m[s] = (m[s] || 0) + 1;
    });
    return m;
  }, [allBank]);

  /* ---------- Selection helpers ---------- */
  const toggleOne = (id, checked) => {
    setSelectedIds((prev) => {
      const s = new Set(prev);
      checked ? s.add(id) : s.delete(id);
      return s;
    });
  };
  const toggleAllOnPage = (checked) => {
    setSelectAllOnPage(checked);
    setSelectedIds((prev) => {
      const s = new Set(prev);
      allBank.forEach((q) => (checked ? s.add(q.id) : s.delete(q.id)));
      return s;
    });
  };
  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectAllOnPage(false);
  };

  /* ---------- Add to existing template (clone → /schools/{sid}/questions) ---------- */
  const addToTemplate = async (templateId) => {
    if (!schoolId) return;
    if (!templateId) return showToast("Pick a template first.");
    if (selectedIds.size === 0) return showToast("Select at least one question.");

    try {
      const qsRef = collection(db, "schools", schoolId, "questions");

      // Load existing questions of this template to dedupe
      const existingSnap = await getDocs(query(qsRef, where("templateId", "==", templateId)));
      const existingFP = new Set();
      existingSnap.forEach((d) => {
        const data = d.data() || {};
        existingFP.add(data.dedupeKey || buildFingerprint(data));
      });

      let added = 0, skipped = 0;

      for (const q of allBank) {
        if (!selectedIds.has(q.id)) continue;

        const fp = q.dedupeKey || buildFingerprint(q);
        if (existingFP.has(fp)) { skipped++; continue; }

        // Minimal fields required by /schools/{sid}/questions rules
        const marks = typeof q.marks === "number" ? q.marks : 1;
        const negativeMarks = typeof q.negativeMarks === "number" ? q.negativeMarks : 0;

        const {
          id, _src, _fp, ownerSchoolId, sourceSchools, linkedBy,
          createdAt, updatedAt, templateId: _ignoreTpl, ...rest
        } = q;

        const targetRef = doc(db, "schools", schoolId, "questions", stableDocId(templateId, fp));
        const exists = (await getDoc(targetRef)).exists();
        if (exists) { skipped++; continue; }

        await setDoc(
          targetRef,
          {
            ...rest,
            templateId,
            marks,
            negativeMarks,
            dedupeKey: fp,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: false }
        );
        existingFP.add(fp);
        added++;

        // If the item came from SHARED bank, link our schoolId into its sourceSchools
        if (_src === "shared") {
          try {
            const sharedRef = doc(db, "questionbank", id);
            const snap = await getDoc(sharedRef);
            if (snap.exists()) {
              const curr = snap.data() || {};
              const currList = Array.isArray(curr.sourceSchools) ? curr.sourceSchools : [];
              if (!currList.includes(schoolId)) {
                const next = [...currList, schoolId];
                await updateDoc(sharedRef, {
                  sourceSchools: next,
                  updatedAt: serverTimestamp(),
                  linkedBy: { schoolId, at: serverTimestamp() }
                });
              }
            }
          } catch (_) { /* non-fatal */ }
        }
      }

      clearSelection();
      showToast(`Added ${added}. Skipped ${skipped} duplicate${skipped === 1 ? "" : "s"}.`);
    } catch (e) {
      showToast(e?.message || "Failed to add questions.");
    }
  };

  /* ---------- Create template from selection ---------- */
  const createTemplateFromSelection = async () => {
    if (!schoolId) return;
    if (selectedIds.size === 0) return showToast("Select questions first.");
    if (!newTplName.trim()) return showToast("Enter a template name.");

    try {
      // 1) create template
      const tplRef = await addDoc(collection(db, "schools", schoolId, "examTemplates"), {
        name: newTplName.trim(),
        examType: newTplType,
        status: "draft",
        marksPerCorrect: 1,
        allowNegativeMarking: false,
        marksPerWrong: 0,
        classIds: [],
        totalQuestions: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      const newTemplateId = tplRef.id;

      // 2) clone unique questions into that template
      const seen = new Set();
      let added = 0;

      for (const q of allBank) {
        if (!selectedIds.has(q.id)) continue;

        const fp = q.dedupeKey || buildFingerprint(q);
        if (seen.has(fp)) continue;

        const marks = typeof q.marks === "number" ? q.marks : 1;
        const negativeMarks = typeof q.negativeMarks === "number" ? q.negativeMarks : 0;

        const {
          id, _src, _fp, ownerSchoolId, sourceSchools, linkedBy,
          createdAt, updatedAt, templateId: _ignoreTpl, ...rest
        } = q;

        const targetRef = doc(db, "schools", schoolId, "questions", stableDocId(newTemplateId, fp));
        const exists = (await getDoc(targetRef)).exists();
        if (exists) continue;

        await setDoc(targetRef, {
          ...rest,
          templateId: newTemplateId,
          marks,
          negativeMarks,
          dedupeKey: fp,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        // link shared doc to this school
        if (_src === "shared") {
          try {
            const sharedRef = doc(db, "questionbank", id);
            const snap = await getDoc(sharedRef);
            if (snap.exists()) {
              const curr = snap.data() || {};
              const currList = Array.isArray(curr.sourceSchools) ? curr.sourceSchools : [];
              if (!currList.includes(schoolId)) {
                const next = [...currList, schoolId];
                await updateDoc(sharedRef, {
                  sourceSchools: next,
                  updatedAt: serverTimestamp(),
                  linkedBy: { schoolId, at: serverTimestamp() }
                });
              }
            }
          } catch (_) { /* ignore */ }
        }

        seen.add(fp);
        added++;
      }

      // 3) quick count
      await setDoc(
        doc(db, "schools", schoolId, "examTemplates", newTemplateId),
        { totalQuestions: added, updatedAt: serverTimestamp() },
        { merge: true }
      );

      setNewTplOpen(false);
      clearSelection();
      showToast(`Template created & ${added} question${added === 1 ? "" : "s"} added.`);
      nav(`/school/exams/templates/${newTemplateId}/questions`);
    } catch (e) {
      showToast(e?.message || "Failed to create template.");
    }
  };

  if (schoolLoading) {
    return <div className="d-flex align-items-center justify-content-center py-5"><Spinner /></div>;
  }

  return (
    <div className="container-fluid p-0">
      {/* Toast */}
      <ToastContainer position="bottom-end" className="p-3">
        <Toast bg="dark" onClose={() => setToast({ show: false, msg: "" })} show={toast.show} delay={2200} autohide>
          <Toast.Body className="text-white">{toast.msg}</Toast.Body>
        </Toast>
      </ToastContainer>

      <Row className="g-3">
        <Col xs={12}>
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="m-0">Question Bank</h4>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" onClick={() => nav("/school/exams")}>Back</Button>
            </div>
          </div>
        </Col>

        {!isActiveSchool && (
          <Col xs={12}>
            <Alert variant="warning" className="mb-0">School inactive — adding to templates is disabled.</Alert>
          </Col>
        )}

        {/* Top stats */}
        <Col xs={12}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex flex-wrap align-items-center gap-3">
                <div className="fw-semibold">
                  Total (after filters & de-dup): <Badge bg="secondary">{loading ? "…" : allBank.length}</Badge>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {Object.entries(subjectCounts).map(([s, n]) => (
                    <Badge key={s} bg="light" text="dark">{s}: {n}</Badge>
                  ))}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Filters + bulk actions */}
        <Col xs={12}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <Row className="g-3 align-items-end">
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Class</Form.Label>
                    <Form.Select value={fClass} onChange={(e) => setFClass(e.target.value)}>
                      <option value="">All</option>
                      {classOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Subject</Form.Label>
                    <Form.Select value={fSubject} onChange={(e) => setFSubject(e.target.value)}>
                      <option value="">All</option>
                      {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Chapter</Form.Label>
                    <Form.Select value={fChapter} onChange={(e) => setFChapter(e.target.value)}>
                      <option value="">All</option>
                      {chapterOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Topic</Form.Label>
                    <Form.Select value={fTopic} onChange={(e) => setFTopic(e.target.value)}>
                      <option value="">All</option>
                      {topicOptions.map((t) => <option key={t} value={t}>{t}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Type</Form.Label>
                    <Form.Select value={fType} onChange={(e) => setFType(e.target.value)}>
                      <option value="">All</option>
                      <option value="mcq">MCQ</option>
                      <option value="multi_correct">MCQ (Multi)</option>
                      <option value="true_false">True/False</option>
                      <option value="descriptive">Descriptive</option>
                      <option value="match">Match</option>
                      <option value="assertion_reason">Assertion/Reason</option>
                      <option value="statement_based">Statement</option>
                      <option value="case_based">Case</option>
                      <option value="mcq_descriptive">MCQ + Desc</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Difficulty</Form.Label>
                    <Form.Select value={fDiff} onChange={(e) => setFDiff(e.target.value)}>
                      <option value="">All</option>
                      {DIFFICULTY.map((d) => <option key={d} value={d}>{d}</option>)}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Search</Form.Label>
                    <InputGroup>
                      <Form.Control
                        placeholder="class / subject / chapter / topic / content…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => { setSearch(""); setFClass(""); setFSubject(""); setFChapter(""); setFTopic(""); setFType(""); setFDiff(""); }}
                      >
                        Clear
                      </Button>
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Source</Form.Label>
                    <div className="d-flex align-items-center gap-4">
                      <Form.Check
                        type="switch"
                        id="srcShared"
                        label="From Question Bank"
                        checked={showShared}
                        onChange={(e) => setShowShared(e.target.checked)}
                      />
                      <Form.Check
                        type="switch"
                        id="srcLocal"
                        label="School Questions"
                        checked={showLocal}
                        onChange={(e) => setShowLocal(e.target.checked)}
                      />
                    </div>
                  </Form.Group>
                </Col>

                {/* Bulk actions */}
                <Col xs={12} className="d-flex flex-wrap gap-2">
                  <Form.Check
                    type="checkbox"
                    id="selectAll"
                    label="Select all in view"
                    checked={selectAllOnPage}
                    onChange={(e) => toggleAllOnPage(e.target.checked)}
                  />

                  {/* Add to an existing template */}
                  <Dropdown as={ButtonGroup}>
                    <Button
                      variant="primary"
                      disabled={!isActiveSchool || selectedIds.size === 0 || tplLoading}
                      onClick={() => {
                        if ((templates || []).length === 1) addToTemplate(templates[0].id);
                      }}
                    >
                      Add to Template ({selectedIds.size})
                    </Button>
                    <Dropdown.Toggle split variant="primary" id="dropdown-split-basic" disabled={!isActiveSchool || tplLoading} />
                    <Dropdown.Menu>
                      {templates.length === 0 && (
                        <Dropdown.Item disabled>No templates</Dropdown.Item>
                      )}
                      {templates.map((t) => (
                        <Dropdown.Item key={t.id} onClick={() => addToTemplate(t.id)}>
                          {t.name} · <small>{String(t.status || "draft")}</small>
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>

                  {/* Create template from selection */}
                  <Button
                    variant="outline-success"
                    disabled={!isActiveSchool || selectedIds.size === 0}
                    onClick={() => setNewTplOpen(true)}
                  >
                    Create Template from Selection
                  </Button>

                  {selectedIds.size > 0 && (
                    <Button variant="outline-secondary" onClick={clearSelection}>
                      Clear selection
                    </Button>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* Table */}
        <Col xs={12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span className="fw-semibold">Questions ({allBank.length})</span>
              <div className="text-muted small">
                {(showShared ? "Shared" : "")}{(showShared && showLocal) ? " + " : ""}{(showLocal ? "School" : "")}
                {" · "}{fClass || "All classes"}
                {fSubject && <> · {fSubject}</>}
                {fChapter && <> · {fChapter}</>}
                {fTopic && <> · {fTopic}</>}
                {fType && <> · {TYPE_LABEL(fType)}</>}
                {fDiff && <> · {fDiff}</>}
                {search && <> · “{search}”</>}
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {loading ? (
                <div className="p-4 text-center"><Spinner /></div>
              ) : allBank.length === 0 ? (
                <div className="p-4 text-muted">No questions match.</div>
              ) : (
                <Table hover responsive className="mb-0 align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>
                        <Form.Check
                          type="checkbox"
                          checked={selectAllOnPage}
                          onChange={(e) => toggleAllOnPage(e.target.checked)}
                        />
                      </th>
                      <th>Meta</th>
                      <th style={{ minWidth: 380 }}>Question · Options · Answer · Explanation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allBank.map((q) => (
                      <tr key={q.id}>
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={selectedIds.has(q.id)}
                            onChange={(e) => toggleOne(q.id, e.target.checked)}
                          />
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          <div className="d-flex flex-column gap-1">
                            <div>
                              {getClassLabel(q) && <Badge bg="light" text="dark" className="me-1">{getClassLabel(q)}</Badge>}
                              {q.subject && <Badge bg="light" text="dark" className="me-1">{q.subject}</Badge>}
                              {getChapterLabel(q) && <Badge bg="light" text="dark" className="me-1">Ch: {getChapterLabel(q)}</Badge>}
                            </div>
                            <div>
                              {getTopicLabel(q) && <Badge bg="light" text="dark" className="me-1">{getTopicLabel(q)}</Badge>}
                              <Badge bg="light" text="dark" className="me-1">{TYPE_LABEL(q.questionType)}</Badge>
                              <Badge bg="light" text="dark" className="me-1">{q.difficulty || "-"}</Badge>
                              <Badge bg="light" text="dark" className="me-1">
                                {(typeof q.marks === "number" ? q.marks : 1)}
                                {typeof q.negativeMarks === "number" && q.negativeMarks !== 0 ? ` / ${q.negativeMarks}` : ""}
                              </Badge>
                              <Badge bg={q._src === "shared" ? "primary" : "secondary"}>{q._src === "shared" ? "Shared" : "School"}</Badge>
                            </div>
                          </div>
                        </td>
                        <td style={{ maxWidth: 900 }}>
                          <QuestionDetails q={q} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Create Template Modal */}
      <Modal show={newTplOpen} onHide={() => setNewTplOpen(false)} centered>
        <Modal.Header closeButton><Modal.Title>Create Template from Selection</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Template Name</Form.Label>
            <Form.Control
              placeholder="e.g., Term 1 — Physics"
              value={newTplName}
              onChange={(e) => setNewTplName(e.target.value)}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Exam Type</Form.Label>
            <Form.Select value={newTplType} onChange={(e) => setNewTplType(e.target.value)}>
              <option value="objective">Objective</option>
              <option value="descriptive">Descriptive</option>
              <option value="mixed">Mixed</option>
            </Form.Select>
          </Form.Group>
          <Form.Text className="text-muted d-block mt-2">
            You can attach classes/sections later from the template or Exams page.
          </Form.Text>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setNewTplOpen(false)}>Cancel</Button>
          <Button variant="success" onClick={createTemplateFromSelection} disabled={!isActiveSchool}>
            Create
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
